import { addressCarriesPerson, isBusinessPortalPath } from '~/utils/person-address';

export default defineNuxtRouteMiddleware(async (to, from) => {
    const authStore = useAuthStore();

    // Entering the business portal ends any impersonation, however the member gets there: a sidebar switcher, a Back, or
    // an address typed or bookmarked. It is enforced here rather than in `middleware/business.ts` because this global
    // runs on every navigation, and only the pages that declare `middleware: ['business']` run that one.
    const isBusinessRoute = isBusinessPortalPath(to.path);

    if (isBusinessRoute && typeof window !== 'undefined') {
        const personStore = usePersonStore();

        // A switch still reading has not changed the person on screen yet, so `impersonating` below cannot see it, and
        // its read would put that person on screen while this navigation lands in the portal. Said here, before this
        // navigation awaits anything (`business.ts` awaits the company), it leaves the person on screen as they are
        // when the read lands (`stores/person.ts`).
        personStore.invalidatePersonFetch();

        // A clean business address alone would leave the impersonated person on screen, and a later Back to a consumer
        // page whose address names no one would show them under it. `endImpersonationTo` clears the person and the
        // vault and loads the business address afresh, so the app boots as the signed-in member.
        if (personStore.impersonating) {
            // Built a pair at a time rather than through `URLSearchParams(to.query)`.
            // Vue Router hands over `string | null | (string | null)[]`: a repeated
            // `?tag=a&tag=b` is an array, which that constructor would join into
            // `tag=a%2Cb`, and a valueless `?flag` is null, which it would write out
            // as the literal `flag=null`. Ending impersonation would then quietly
            // rewrite filters it has no business touching.
            const pairs: string[] = [];

            for (const [key, value] of Object.entries(to.query)) {
                if (key === 'person') continue;

                for (const one of Array.isArray(value) ? value : [value]) {
                    pairs.push(
                        one === null ? encodeURIComponent(key) : `${encodeURIComponent(key)}=${encodeURIComponent(one)}`,
                    );
                }
            }

            const search = pairs.join('&');

            personStore.endImpersonationTo(`${to.path}${search ? `?${search}` : ''}${to.hash}`);
            return abortNavigation();
        }
    }

    // No business address names a person. One that does with no one impersonated on screen (an address typed or
    // bookmarked) loses `?person`.
    if (isBusinessRoute && to.query.person) {
        const query = { ...to.query };
        delete query.person;
        // `hash` has to be named: a location built from path and query alone
        // resolves to an empty fragment, so the strip would also throw away the
        // section the URL pointed at. `pages/business/[slug]/index.vue` and
        // `useUpgradeReturnNotice` carry the fragment through for the same reason.
        return navigateTo({ path: to.path, query, hash: to.hash });
    }

    // Every other navigation inside the app keeps the person the page being left names, so the address names who is on
    // screen. One that begins while a switch is under way is filled from a page the switch is replacing, and the person
    // store (`stores/person.ts`) carries the person the switch puts on screen onto an address this leaves naming no one.
    if (addressCarriesPerson(to.path) && from.query.person && !to.query.person) {
        return navigateTo({
            path: to.path,
            query: { ...to.query, person: from.query.person }
        });
    }

    // Check if the route exists, otherwise redirect to not-found
    const router = useRouter();
    const resolved = router.resolve(to.fullPath);
    // If no matched route and not already on /not-found, redirect
    if (!resolved.matched.length && to.path !== '/not-found') {
        return navigateTo('/not-found');
    }

    // Check if page explicitly marks itself as public
    const isPagePublic = to.meta.public === true;

    // A page that needs a session shows the member this document's /me confirmed and the person on screen. The boot
    // confirms both (plugins/hooks.ts). A navigation that finds no member confirmed (the boot's /me went unanswered, or
    // the boot found no session) signs in here first and loads the person the address names, as the boot does. It fails
    // while /me goes unanswered, so no page that needs a session opens with no one: the error page on a first load, and
    // the page the member is on stays otherwise.
    if (!isPagePublic && !authStore.user && (await authStore.initializeAuth())) {
        await usePersonStore().loadPersonFromAddress();
    }

    const isAuthenticated = authStore.loggedIn;

    // If not a public route and user is not authenticated, redirect to login
    if (!isPagePublic && !isAuthenticated) {
        // Set redirect cookie for where to go after login (path + query only)
        const redirectCookie = useCookie('pb-redirect');
        redirectCookie.value = to.fullPath;
        
        // Carry the params the auth app acts on. `pb-redirect` above holds the
        // full path, but it is a cookie the auth app never reads — anything the
        // auth app itself needs has to travel on the URL.
        const loginQuery = new URLSearchParams();
        if (to.query.email) {
            loginQuery.set('email', to.query.email as string);
        }
        if (to.query.eraseme === '1') {
            loginQuery.set('eraseme', '1');
        }

        const loginSearch = loginQuery.toString();
        return navigateTo(loginSearch ? `/auth/login?${loginSearch}` : '/auth/login');
    }

    // If user is authenticated but profile is not ready, redirect
    // to auth app for profile completion
    if (isAuthenticated && !isPagePublic && authStore.user && !authStore.user.identity.profile_ready) {
        const config = useRuntimeConfig();
        const redirectUrl = `${window.location.origin}${to.fullPath}`;
        await navigateTo(`${config.public.pb.authApp}?redirect_url=${encodeURIComponent(redirectUrl)}`, { external: true });
        return;
    }

    // Handle root path redirect
    if (to.path === '/') {
        if (isAuthenticated) {
            // Preserve query parameters when redirecting to dashboard
            return navigateTo({
                path: '/dashboard',
                query: to.query
            });
        } else {
            // Set redirect cookie for where to go after login (path + query only)
            const redirectCookie = useCookie('pb-redirect');
            redirectCookie.value = to.fullPath;
            
            // Build login URL with email parameter if it exists
            let loginUrl = `/auth/login`;
            if (to.query.email) {
                loginUrl += `?email=${encodeURIComponent(to.query.email as string)}`;
            }
            
            return navigateTo(loginUrl);
        }
    }
});
